import { RailChipTabs } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import type { FieldPassesModel } from './useFieldPassesModel'
import type { FieldPassRecord } from '../../types/fieldPass'

const statusTone = (status: string) => {
  if (status === 'ACTIVE' || status === 'REDEEMED') return 'border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'
  if (status === 'REVOKED') return 'border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]'
  return 'border-[var(--border)] bg-[var(--surface-raised)] text-[var(--text)]'
}

const toMs = (value: unknown): number | null => {
  if (!value || typeof value !== 'object' || typeof (value as { toMillis?: unknown }).toMillis !== 'function') return null
  return (value as { toMillis: () => number }).toMillis()
}

const formatDate = (value: unknown): string => {
  const ms = toMs(value)
  if (!ms) return 'Not set'
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

type Props = {
  model: FieldPassesModel
  canManage: boolean
  selectedPassId: string | null
  onSelect: (pass: FieldPassRecord) => void
  hideCreateAction?: boolean
}

export const FieldPassesListing = ({ model, canManage, selectedPassId, onSelect, hideCreateAction = false }: Props) => {
  if (!canManage) {
    return (
      <div className="engagementEmpty">
        <strong>Field Passes are restricted</strong>
        <p>Ask a workspace admin for invite rights to manage temporary field access.</p>
      </div>
    )
  }

  return (
    <div className="fieldPassesListing flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RailChipTabs
          orientation="horizontal"
          ariaLabel="Field pass view"
          value={model.view}
          onChange={(key) => model.setView(key as 'ACTIVE' | 'ARCHIVED')}
          items={[
            {
              key: 'ACTIVE',
              label: 'Active & upcoming',
              count: model.activePasses.length,
            },
            {
              key: 'ARCHIVED',
              label: 'Archived',
              count: model.archivedPasses.length,
            },
          ]}
        />
        {!hideCreateAction ? (
          <Button variant="primary" size="sm" onClick={() => model.startCreate()}>
            + New Field Pass
          </Button>
        ) : null}
      </div>

      {model.recordsUnavailable ? (
        <p className="rounded-lg border border-[color-mix(in_srgb,var(--warning)_30%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] p-3 text-xs text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
          Existing Field Pass records are unavailable right now. New passes can still be created and copied.
        </p>
      ) : null}

      {model.visiblePasses.length === 0 && !model.recordsUnavailable ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[var(--border)] bg-[var(--surface)] p-6 text-center">
          <h3 className="text-base font-semibold text-[var(--text-strong)]">
            {model.view === 'ACTIVE' ? 'No active Field Passes' : 'No archived Field Passes'}
          </h3>
          <p className="max-w-sm text-sm text-[var(--muted)]">
            {model.view === 'ACTIVE'
              ? 'Create a temporary burner pass so a helper can pick up one or more checklist tasks without a permanent seat.'
              : 'Expired, revoked, and completed burners appear here for 30 days.'}
          </p>
        </div>
      ) : null}

      <div className="grid gap-2">
        {model.visiblePasses.map((pass) => {
          const selected = selectedPassId === pass.id
          return (
            <Pressable
              key={pass.id}
              aria-pressed={selected}
              onClick={() => onSelect(pass)}
              className={`fieldPassesRow${selected ? ' isSelected' : ''}`}
            >
              <div>
                <p className="text-sm font-semibold text-[var(--text-strong)]">
                  {pass.displayName ? `Field Pass - ${pass.displayName}` : 'Unredeemed Field Pass'}
                </p>
                <p className="mt-1 text-[11px] text-[var(--muted)]">{pass.targets.map((target) => target.title).join(', ') || 'No targets'}</p>
                {model.view === 'ACTIVE' ? (
                  <p className="mt-1 text-[11px] text-[var(--muted)]">Expires {formatDate(pass.expiresAt)}</p>
                ) : null}
              </div>
              <span className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${statusTone(pass.status)}`}>
                {pass.status}
              </span>
            </Pressable>
          )
        })}
      </div>
    </div>
  )
}

export default FieldPassesListing
